import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, FileText, Link as LinkIcon, Edit, Save, Palette } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DataSourceView = () => {
  const navigate = useNavigate();
  const { sourceId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [source, setSource] = useState(null);
  const [editedContent, setEditedContent] = useState("");

  useEffect(() => {
    fetchSource();
  }, [sourceId]);

  const fetchSource = async () => {
    try {
      const response = await authAxios.get(`/data-sources/${sourceId}`);
      setSource(response.data);
      setEditedContent(response.data.content || response.data.scraped_content || "");
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data source:", error);
      toast.error("Data source not found");
      navigate(-1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put(`/data-sources/${sourceId}`, {
        content: editedContent
      });
      
      setSource({ ...source, content: editedContent });
      setIsEditing(false);
      toast.success("Data source updated!");
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Navigate back to the relationship's data sources tab
    if (source?.owner_type === "relationship" && source?.owner_id) {
      navigate(`/relationships/${source.owner_id}`, { state: { activeTab: 'data-sources' } });
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  const content = source?.content || source?.scraped_content || "";
  const isScraped = source?.source_method === "url_scraped";

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              {source?.data_type === "match_style_card" || source?.data_type === "user_style_card" ? (
                <Palette className="w-6 h-6 text-green-400" />
              ) : isScraped ? (
                <LinkIcon className="w-6 h-6 text-cyan-400" />
              ) : (
                <FileText className={`w-6 h-6 ${source?.auto_imported ? 'text-cyan-400' : 'text-divine-gold'}`} />
              )}
              <div>
                <h1 className="font-cinzel text-xl text-white">{source?.title}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-white/40 text-sm">
                    {source?.char_count?.toLocaleString() || 0} characters
                  </p>
                  {(source?.data_type === "match_style_card" || source?.data_type === "user_style_card") && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30 font-medium">
                      Style Card
                    </span>
                  )}
                  {source?.auto_imported && source?.data_type !== "match_style_card" && source?.data_type !== "user_style_card" && (
                    <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">
                      Auto-Imported
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Edit/Save Buttons */}
            <div className="ml-auto flex items-center gap-3">
              {!source?.auto_imported && (
                isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditedContent(source?.content || source?.scraped_content || "");
                        setIsEditing(false);
                      }}
                      className="border-white/20 text-white hover:bg-white/5"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-divine-gold hover:bg-divine-gold-light text-black"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardHeader>
            <CardTitle className="text-divine-gold font-cinzel">Content</CardTitle>
            {source?.ref_url && (
              <a
                href={source.ref_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-2"
              >
                <LinkIcon className="w-3 h-3" />
                {source.ref_url}
              </a>
            )}
            {source?.scrape_url && (
              <a
                href={source.scrape_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-2"
              >
                <LinkIcon className="w-3 h-3" />
                {source.scrape_url}
              </a>
            )}
            {source?.description && (
              <p className="text-white/60 text-sm mt-2">{source.description}</p>
            )}
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="min-h-[500px] bg-black border-divine-gold/20 text-white font-mono text-sm"
                placeholder="Enter content..."
              />
            ) : (
              <div className="prose prose-invert max-w-none">
                <div className="text-white/90 leading-relaxed whitespace-pre-wrap bg-divine-black/50 p-6 rounded-lg border border-divine-gold/20 max-h-[700px] overflow-y-auto">
                  {content || "No content available"}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* References Section - Only for STYLE_CARD */}
        {(source?.data_type === "match_style_card" || source?.data_type === "user_style_card") && 
         source?.source_references && source.source_references.length > 0 && (
          <Card className="bg-divine-charcoal border-green-500/20 mt-6">
            <CardHeader>
              <CardTitle className="text-green-400 font-cinzel text-lg">References</CardTitle>
              <p className="text-white/50 text-sm">Data sources used to generate this style card</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {source.source_references.map((ref, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-green-500/5 rounded-lg border border-green-500/20">
                    <span className="text-white font-medium">{ref.title}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${
                      ref.badge === "Style Analysis" 
                        ? "bg-pink-500/20 text-pink-400 border-pink-500/30"
                        : ref.badge === "Auto"
                          ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                          : "bg-white/10 text-white/60 border-white/20"
                    }`}>
                      {ref.badge}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card className="bg-divine-charcoal border-divine-gold/20 mt-6">
          <CardHeader>
            <CardTitle className="text-divine-gold font-cinzel text-lg">Metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white/60">Created</p>
                <p className="text-white">{new Date(source?.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-white/60">Last Updated</p>
                <p className="text-white">{new Date(source?.updated_at).toLocaleString()}</p>
              </div>
              {isScraped && source?.last_scraped_at && (
                <div>
                  <p className="text-white/60">Last Scraped</p>
                  <p className="text-white">{new Date(source.last_scraped_at).toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-white/60">Source Type</p>
                <p className="text-white">
                  {source?.data_type === "core_match_data" || source?.data_type === "core_user_data"
                    ? "Profile Data Direct"
                    : source?.data_type === "match_style_card" || source?.data_type === "user_style_card"
                      ? `LLM Generated - ${source?.generated_by_model || "Unknown Model"}`
                      : <span className="capitalize">{source?.source_method?.replace('_', ' ')}</span>}
                </p>
              </div>
            </div>
            
            {/* Learning Profile Relevance */}
            {source?.category_relevance && Object.keys(source.category_relevance).length > 0 && (
              <div>
                <p className="text-white/60 mb-3">Learning Profile Relevance</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {Object.entries(source.category_relevance)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, score]) => (
                      <div 
                        key={category} 
                        className={`p-2 rounded border ${
                          score >= 70 ? 'bg-green-500/10 border-green-500/30 text-green-400' :
                          score >= 40 ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' :
                          'bg-white/5 border-white/10 text-white/50'
                        }`}
                      >
                        <span className="text-xs block truncate" title={category.replace(/_/g, ' ')}>
                          {category.replace(/_/g, ' ')}
                        </span>
                        <span className="font-semibold">{score}%</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DataSourceView;
